import {
  ChevronDown,
  ChevronRight,
  Grid3X3,
  Settings2,
  SortAsc,
  Tags,
} from "lucide-react";
import { useState } from "react";
import { twJoin } from "tailwind-merge";

import type {
  ModifierKeywordsState,
  SortMode,
} from "~/components/stations/station-edit-types";
import { isSortMode } from "~/components/stations/station-edit-types";
import { Badge } from "~/components/ui/badge";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "~/components/ui/collapsible";
import { Input } from "~/components/ui/input";
import { Label } from "~/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "~/components/ui/select";
import { Switch } from "~/components/ui/switch";
import { parseEnumValue } from "~/lib/parse-enum-value";

interface KdsAdvancedSettingsSectionProps {
  sortMode: SortMode;
  setSortMode: (mode: SortMode) => void;
  createTicketPerSend: boolean;
  setCreateTicketPerSend: (value: boolean) => void;
  modifierKeywords: ModifierKeywordsState;
  setModifierKeywords: (keywords: ModifierKeywordsState) => void;
  isOpen: boolean;
  setIsOpen: (open: boolean) => void;
}

export function KdsAdvancedSettingsSection({
  sortMode,
  setSortMode,
  createTicketPerSend,
  setCreateTicketPerSend,
  modifierKeywords,
  setModifierKeywords,
  isOpen,
  setIsOpen,
}: KdsAdvancedSettingsSectionProps) {
  const updateAllergyKeywords = (keywords: string[]) => {
    setModifierKeywords({ ...modifierKeywords, allergyKeywords: keywords });
  };

  const updateRemoveKeywords = (keywords: string[]) => {
    setModifierKeywords({ ...modifierKeywords, removeKeywords: keywords });
  };

  const updateAddKeywords = (keywords: string[]) => {
    setModifierKeywords({ ...modifierKeywords, addKeywords: keywords });
  };

  return (
    <Collapsible onOpenChange={setIsOpen} open={isOpen}>
      <CollapsibleTrigger asChild>
        <div className="border-border hover:bg-accent/50 flex cursor-pointer items-center justify-between rounded-xl border px-4 py-3">
          <div className="flex items-center gap-3">
            <Settings2 className="text-muted-foreground h-5 w-5" />
            <div>
              <p className="font-medium">Advanced Settings</p>
              <p className="text-muted-foreground text-sm">
                Sorting, keywords, ticket behavior
              </p>
            </div>
          </div>
          {isOpen ? (
            <ChevronDown className="text-muted-foreground h-5 w-5" />
          ) : (
            <ChevronRight className="text-muted-foreground h-5 w-5" />
          )}
        </div>
      </CollapsibleTrigger>
      <CollapsibleContent>
        <div className="border-border bg-accent/30 mt-2 space-y-4 rounded-xl border p-4">
          {/* Sort Mode */}
          <div className="flex flex-col gap-2">
            <div className="flex items-center gap-2">
              <SortAsc className="text-muted-foreground h-4 w-4" />
              <Label>Order Sorting</Label>
            </div>
            <Select
              onValueChange={(v) =>
                setSortMode(parseEnumValue(v, isSortMode, "fifo"))
              }
              value={sortMode}
            >
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="fifo">FIFO - First In, First Out</SelectItem>
                <SelectItem value="rule-based">
                  Rule-Based - Priority sorting
                </SelectItem>
                <SelectItem value="hybrid">
                  Hybrid - FIFO with priority boosts
                </SelectItem>
              </SelectContent>
            </Select>
          </div>

          {/* Ticket Behavior */}
          <button
            className="bg-background flex w-full cursor-pointer items-center justify-between rounded-lg px-3 py-2 text-left"
            onClick={() => setCreateTicketPerSend(!createTicketPerSend)}
            type="button"
          >
            <div className="flex items-center gap-2">
              <Grid3X3 className="text-muted-foreground h-4 w-4" />
              <div>
                <p className="text-sm font-medium">Create Ticket Per Send</p>
                <p className="text-muted-foreground text-xs">
                  New ticket for each order send vs. append to existing
                </p>
              </div>
            </div>
            <Switch checked={createTicketPerSend} />
          </button>

          {/* Modifier Keywords */}
          <div className="space-y-3">
            <div className="flex items-center gap-2">
              <Tags className="text-muted-foreground h-4 w-4" />
              <Label>Modifier Keywords</Label>
            </div>
            <div className="space-y-2">
              <KeywordInput
                color="text-destructive"
                keywords={modifierKeywords.allergyKeywords}
                label="Allergy Keywords"
                onChange={updateAllergyKeywords}
              />
              <KeywordInput
                color="text-warning"
                keywords={modifierKeywords.removeKeywords}
                label="Remove Keywords"
                onChange={updateRemoveKeywords}
              />
              <KeywordInput
                color="text-success"
                keywords={modifierKeywords.addKeywords}
                label="Add Keywords"
                onChange={updateAddKeywords}
              />
            </div>
          </div>
        </div>
      </CollapsibleContent>
    </Collapsible>
  );
}

function KeywordInput({
  label,
  color,
  keywords,
  onChange,
}: {
  label: string;
  color: string;
  keywords: string[];
  onChange: (keywords: string[]) => void;
}) {
  const [inputValue, setInputValue] = useState("");

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter" && inputValue.trim()) {
      e.preventDefault();
      if (!keywords.includes(inputValue.trim().toLowerCase())) {
        onChange([...keywords, inputValue.trim().toLowerCase()]);
      }
      setInputValue("");
    }
  };

  const removeKeyword = (keyword: string) => {
    onChange(keywords.filter((k) => k !== keyword));
  };

  return (
    <div className="bg-background rounded-lg p-2">
      <div className="flex items-center gap-2">
        <span className={twJoin("font-medium text-xs", color)}>{label}</span>
      </div>
      <div className="mt-2 flex flex-wrap gap-1">
        {keywords.map((keyword) => (
          <Badge
            className="cursor-pointer gap-1"
            key={keyword}
            onClick={() => removeKeyword(keyword)}
            variant="secondary"
          >
            {keyword}
            <span className="text-muted-foreground hover:text-foreground">
              x
            </span>
          </Badge>
        ))}
        <Input
          className="h-6 w-24 border-none bg-transparent px-1 text-xs shadow-none focus-visible:ring-0"
          onChange={(e) => setInputValue(e.target.value)}
          onKeyDown={handleKeyDown}
          placeholder="Add keyword..."
          value={inputValue}
        />
      </div>
    </div>
  );
}
